// src/pages/admin/RoleMaster.tsx - Shopify-style design with Slidemenu and Header
import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  TextField,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Stack,
  Alert,
  Snackbar,
  useMediaQuery,
  useTheme,
  Drawer,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SecurityIcon from '@mui/icons-material/Security';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useAddRoleMutation,
  useListRolesQuery,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  Role,
} from '../../features/admin/roleMasterApi.ts';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

const RoleMaster = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [formData, setFormData] = useState({
    role_name: '',
    role_desc: '',
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  // RTK Query hooks
  const { data: rolesData, isLoading: isLoadingRoles, refetch } = useListRolesQuery();
  const [addRole, { isLoading: isAdding }] = useAddRoleMutation();
  const [updateRole, { isLoading: isUpdating }] = useUpdateRoleMutation();
  const [deleteRole, { isLoading: isDeleting }] = useDeleteRoleMutation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleOpenDialog = (role?: Role) => {
    if (role) {
      setEditMode(true);
      setSelectedRole(role);
      setFormData({
        role_name: role.role_name,
        role_desc: role.role_desc || '',
      });
    } else {
      setEditMode(false);
      setSelectedRole(null);
      setFormData({
        role_name: '',
        role_desc: '',
      });
    }
    setOpenDialog(true);
    setFormErrors({});
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setSelectedRole(null);
    setFormData({
      role_name: '',
      role_desc: '',
    });
    setFormErrors({});
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.role_name.trim()) {
      newErrors.role_name = 'Role name is required';
    }

    if (!formData.role_desc.trim()) {
      newErrors.role_desc = 'Role description is required';
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      if (editMode && selectedRole) {
        const result = await updateRole({
          id: selectedRole.id,
          role_name: formData.role_name.trim(),
          role_desc: formData.role_desc.trim(),
        }).unwrap();

        if (!result.error) {
          setSnackbar({
            open: true,
            message: result.message || 'Role updated successfully',
            severity: 'success',
          });
          setTimeout(() => {
            handleCloseDialog();
            refetch();
          }, 1000);
        } else {
          setSnackbar({
            open: true,
            message: result.message || 'Failed to update role',
            severity: 'error',
          });
        }
      } else {
        const result = await addRole({
          role_name: formData.role_name.trim(),
          role_desc: formData.role_desc.trim(),
        }).unwrap();

        if (!result.error) {
          setSnackbar({
            open: true,
            message: result.message || 'Role added successfully',
            severity: 'success',
          });
          setTimeout(() => {
            handleCloseDialog();
            refetch();
          }, 1000);
        } else {
          setSnackbar({
            open: true,
            message: result.message || 'Failed to add role',
            severity: 'error',
          });
        }
      }
    } catch (error: any) {
      console.error('Role operation error:', error);
      setSnackbar({
        open: true,
        message: error?.data?.message || (editMode ? 'Failed to update role' : 'Failed to add role'),
        severity: 'error',
      });
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this role?')) {
      try {
        const result = await deleteRole(id).unwrap();
        if (!result.error) {
          setSnackbar({
            open: true,
            message: result.message || 'Role deleted successfully',
            severity: 'success',
          });
          setTimeout(() => {
            refetch();
          }, 1000);
        } else {
          setSnackbar({
            open: true,
            message: result.message || 'Failed to delete role',
            severity: 'error',
          });
        }
      } catch (error: any) {
        console.error('Delete role error:', error);
        setSnackbar({
          open: true,
          message: error?.data?.message || 'Failed to delete role',
          severity: 'error',
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString.replace(' ', 'T'));
      return (
        date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        }) +
        ', ' +
        date.toLocaleTimeString('en-GB', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        })
      );
    } catch (error) {
      console.error('Date parsing error:', error);
      return dateString;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Desktop Sidebar */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />

          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              backgroundColor: '#f1f2f4',
            }}
          >
            <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <SecurityIcon sx={{ fontSize: 28, color: '#008060' }} />
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    Role Master
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                  sx={{
                    textTransform: 'none',
                    bgcolor: '#008060',
                    fontWeight: 600,
                    borderRadius: '8px',
                    px: 2.5,
                    '&:hover': { bgcolor: '#006e52' },
                  }}
                >
                  Add Role
                </Button>
              </Box>

              {/* Roles Table */}
              <Card
                sx={{
                  borderRadius: '16px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  border: '1px solid #e1e3e5',
                  overflow: 'hidden',
                }}
              >
                {isLoadingRoles ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: 300,
                      bgcolor: 'white',
                    }}
                  >
                    <CircularProgress size={32} sx={{ color: '#008060' }} />
                  </Box>
                ) : rolesData?.roles && rolesData.roles.length > 0 ? (
                  <Box sx={{ bgcolor: 'white', overflowX: 'auto' }}>
                    {/* Table Header */}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '80px 1fr 2fr 200px 200px 120px',
                        gap: 2,
                        px: 3,
                        py: 1.5,
                        borderBottom: '1px solid #e1e3e5',
                        bgcolor: '#fafbfb',
                        minWidth: '900px',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6d7175',
                          fontWeight: 600,
                          fontSize: '11px',
                          textTransform: 'uppercase',
                        }}
                      >
                        S.No
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6d7175',
                          fontWeight: 600,
                          fontSize: '11px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Role Name
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6d7175',
                          fontWeight: 600,
                          fontSize: '11px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Description
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6d7175',
                          fontWeight: 600,
                          fontSize: '11px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Created Date
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6d7175',
                          fontWeight: 600,
                          fontSize: '11px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Updated Date
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6d7175',
                          fontWeight: 600,
                          fontSize: '11px',
                          textTransform: 'uppercase',
                          textAlign: 'right',
                        }}
                      >
                        Actions
                      </Typography>
                    </Box>

                    {/* Table Rows */}
                    {rolesData.roles.map((role, index) => (
                      <Box
                        key={role.id}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '80px 1fr 2fr 200px 200px 120px',
                          gap: 2,
                          px: 3,
                          py: 2,
                          alignItems: 'center',
                          borderBottom: '1px solid #e1e3e5',
                          '&:hover': { bgcolor: '#f9fafb' },
                          '&:last-child': { borderBottom: 'none' },
                          transition: 'background-color 0.15s ease',
                          minWidth: '900px',
                        }}
                      >
                        <Typography variant="body2" sx={{ color: '#6d7175', fontWeight: 500 }}>
                          {index + 1}
                        </Typography>

                        <Box>
                          <Chip
                            label={role.role_name}
                            size="small"
                            sx={{
                              bgcolor: '#e3f2fd',
                              color: '#1976d2',
                              fontWeight: 600,
                              fontSize: '12px',
                            }}
                          />
                        </Box>

                        <Typography variant="body2" color="text.secondary">
                          {role.role_desc || 'N/A'}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                          {formatDate(role.createddt)}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                          {formatDate(role.updateddt)}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(role)}
                            sx={{ color: '#6d7175', '&:hover': { bgcolor: '#f1f2f4' } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(role.id)}
                            disabled={isDeleting}
                            sx={{ color: '#d32f2f', '&:hover': { bgcolor: '#ffebee' } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                    <SecurityIcon sx={{ fontSize: 64, color: '#c4cdd5', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                      No roles found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Click "Add Role" to create your first role
                    </Typography>
                  </Box>
                )}
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '16px' },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #e1e3e5',
            fontWeight: 600,
            color: '#202223',
          }}
        >
          {editMode ? 'Edit Role' : 'Add New Role'}
          <IconButton size="small" onClick={handleCloseDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            <Stack spacing={2.5}>
              <TextField
                fullWidth
                label="Role Name"
                name="role_name"
                value={formData.role_name}
                onChange={handleInputChange}
                error={!!formErrors.role_name}
                helperText={formErrors.role_name || 'Enter role name (e.g., Admin, Manager)'}
                disabled={isAdding || isUpdating}
                placeholder="Enter role name"
                required
              />
              <TextField
                fullWidth
                label="Role Description"
                name="role_desc"
                value={formData.role_desc}
                onChange={handleInputChange}
                error={!!formErrors.role_desc}
                helperText={formErrors.role_desc || 'Enter a brief description of the role'}
                multiline
                rows={3}
                disabled={isAdding || isUpdating}
                placeholder="Enter role description"
                required
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, pt: 2, borderTop: '1px solid #e1e3e5' }}>
            <Button
              onClick={handleCloseDialog}
              disabled={isAdding || isUpdating}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: '#202223',
                borderRadius: '8px',
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isAdding || isUpdating}
              sx={{
                textTransform: 'none',
                bgcolor: '#008060',
                fontWeight: 600,
                borderRadius: '8px',
                px: 2.5,
                '&:hover': { bgcolor: '#006e52' },
              }}
            >
              {isAdding || isUpdating ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} color="inherit" />
                  {editMode ? 'Updating...' : 'Adding...'}
                </Box>
              ) : editMode ? (
                'Update Role'
              ) : (
                'Add Role'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RoleMaster;